/**
 * Pannello di gestione sottoscrizione e licenze del singolo tenant (SuperAdmin MAE).
 *
 * Espone gli endpoint custom v1.8.0:
 *  POST /api/tenants/{id}/subscribe              { planCode, currency, billingPeriod, seats, isTrial }
 *  POST /api/tenants/{id}/cancel-subscription    { reason }
 *  POST /api/tenants/{id}/pause-subscription
 *  POST /api/tenants/{id}/resume-subscription
 *  POST /api/tenants/{id}/upgrade-plan           { planCode }
 *  POST /api/tenants/{id}/apply-promo            { promoCode }
 *  POST /api/tenants/{id}/revoke-promo
 */
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconArrowLeft,
  IconArrowUp,
  IconBuilding,
  IconHistory,
  IconHourglass,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlus,
  IconTag,
  IconX,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from '@mantine/form';
import dayjs from 'dayjs';
import { fetchList, fetchOne } from '@/shared/api/jsonapi/client';
import { httpRequest } from '@/shared/api/client';
import { notifyError, notifySuccess } from '@/shared/lib/notify';
import type { LicensePlan, TenantLicense, TenantSubscription } from '@/shared/types/api.gen';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import type { Tenant } from '@/shared/types/entities';

export const Route = createFileRoute('/_app/admin/tenants/$tenantId/subscription')({ component: SubscriptionPanel });

type SubRow = TenantSubscription & Denormalized & { id: string; plan?: { id: string; code?: string; name?: string } };
type LicenseRow = TenantLicense & Denormalized & { id: string; license?: { id: string; code?: string; name?: string } };
type PlanRow = LicensePlan & Denormalized & { id: string };

interface LicenseHistoryRow extends Denormalized {
  id: string;
  action?: string;
  details?: string | null;
  performedBy?: string | null;
  createdAt?: string;
}

const STATUS_COLOR: Record<string, string> = {
  TRIAL: 'cyan',
  ACTIVE: 'green',
  GRACE_PERIOD: 'yellow',
  PAUSED: 'orange',
  EXPIRED: 'gray',
  SUSPENDED: 'red',
  CANCELLED: 'gray',
};

function SubscriptionPanel() {
  const { tenantId } = useParams({ from: '/_app/admin/tenants/$tenantId/subscription' });
  const qc = useQueryClient();

  const tenantQ = useQuery({
    queryKey: ['tenant', tenantId],
    enabled: Boolean(tenantId),
    queryFn: () => fetchOne<Tenant>('tenants', tenantId),
  });

  const subsQ = useQuery({
    queryKey: ['tenant-subscriptions', tenantId],
    staleTime: 30_000,
    enabled: Boolean(tenantId),
    queryFn: () => fetchList<SubRow>('tenant-subscriptions', {
      filter: { 'tenant.id': { eq: tenantId } },
      include: ['plan'],
      sort: ['-createdAt'],
      page: { size: 20 },
    }),
  });

  const licensesQ = useQuery({
    queryKey: ['tenant-licenses', tenantId],
    staleTime: 30_000,
    enabled: Boolean(tenantId),
    queryFn: () => fetchList<LicenseRow>('tenant-licenses', {
      filter: { 'tenant.id': { eq: tenantId } },
      include: ['license'],
      page: { size: 200 },
    }),
  });

  const historyQ = useQuery({
    queryKey: ['license-histories', tenantId],
    staleTime: 60_000,
    enabled: Boolean(tenantId),
    queryFn: () => fetchList<LicenseHistoryRow>('license-histories', {
      filter: { 'tenant.id': { eq: tenantId } },
      sort: ['-createdAt'],
      page: { size: 30 },
    }),
  });

  const plansQ = useQuery({
    queryKey: ['license-plans-all'],
    staleTime: 5 * 60_000,
    queryFn: () => fetchList<PlanRow>('license-plans', { sort: ['sortOrder'], page: { size: 50 } }),
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['tenant-subscriptions', tenantId] });
    qc.invalidateQueries({ queryKey: ['tenant-licenses', tenantId] });
    qc.invalidateQueries({ queryKey: ['license-histories', tenantId] });
    qc.invalidateQueries({ queryKey: ['active-licenses', tenantId] });
    qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
  };

  const subscribe = useMutation({
    mutationFn: (body: { planCode: string; currency: string; billingPeriod: string; seats: number; isTrial: boolean }) =>
      httpRequest(`/api/tenants/${tenantId}/subscribe`, { method: 'POST', endpointType: 'plain', body }),
    onSuccess: () => { notifySuccess('Sottoscrizione creata'); invalidateAll(); },
    onError: (err) => notifyError(err),
  });

  const lifecycle = useMutation({
    mutationFn: ({ action, reason }: { action: 'cancel' | 'pause' | 'resume'; reason?: string }) => {
      const url =
        action === 'cancel' ? `/api/tenants/${tenantId}/cancel-subscription` :
        action === 'pause' ? `/api/tenants/${tenantId}/pause-subscription` :
        `/api/tenants/${tenantId}/resume-subscription`;
      return httpRequest(url, { method: 'POST', endpointType: 'plain', body: reason ? { reason } : {} });
    },
    onSuccess: (_d, vars) => { notifySuccess(`Sub ${vars.action} eseguito`); invalidateAll(); },
    onError: (err) => notifyError(err),
  });

  const upgrade = useMutation({
    mutationFn: (planCode: string) =>
      httpRequest(`/api/tenants/${tenantId}/upgrade-plan`, { method: 'POST', endpointType: 'plain', body: { planCode } }),
    onSuccess: () => { notifySuccess('Piano aggiornato'); invalidateAll(); },
    onError: (err) => notifyError(err),
  });

  const applyPromo = useMutation({
    mutationFn: (promoCode: string) =>
      httpRequest(`/api/tenants/${tenantId}/apply-promo`, { method: 'POST', endpointType: 'plain', body: { promoCode } }),
    onSuccess: () => { notifySuccess('Promo applicata'); invalidateAll(); },
    onError: (err) => notifyError(err),
  });

  const revokePromo = useMutation({
    mutationFn: async () =>
      httpRequest<unknown>(`/api/tenants/${tenantId}/revoke-promo`, { method: 'POST', endpointType: 'plain' }),
    onSuccess: () => { notifySuccess('Promo revocata'); invalidateAll(); },
    onError: (err) => notifyError(err),
  });

  const [subscribeOpened, subscribeModal] = useDisclosure(false);
  const [promoOpened, promoModal] = useDisclosure(false);
  const promoForm = useForm({ initialValues: { promoCode: '' } });
  const subscribeForm = useForm({
    initialValues: { planCode: '', currency: 'EUR', billingPeriod: 'monthly', seats: 1, isTrial: false },
  });

  if (tenantQ.isLoading) return <Center py="xl"><Loader /></Center>;
  if (!tenantQ.data) return <Title order={3}>Tenant non trovato</Title>;

  const tenant = tenantQ.data;
  const subs = subsQ.data?.data ?? [];
  const currentSub = subs.find((s) => s.status && !['CANCELLED', 'EXPIRED'].includes(s.status));
  const status = currentSub?.status as string | undefined;
  const plans = plansQ.data?.data ?? [];
  const licenses = licensesQ.data?.data ?? [];

  return (
    <Stack>
      <Anchor component={Link} to={`/admin/tenants/${tenantId}`} size="sm">
        <Group gap={4}><IconArrowLeft size={14} /> Torna al dettaglio tenant</Group>
      </Anchor>

      <Group>
        <IconBuilding size={28} />
        <div>
          <Title order={2}>Sottoscrizione · {tenant.name ?? tenantId}</Title>
          <Group gap="xs" mt={4}>
            {status && <Badge color={STATUS_COLOR[status] ?? 'gray'}>{status}</Badge>}
            {currentSub?.plan?.name && <Badge variant="light">{currentSub.plan.name}</Badge>}
            {currentSub?.seats != null && <Text size="xs" c="dimmed">{currentSub.seats} seats</Text>}
          </Group>
        </div>
      </Group>

      <Card withBorder padding="lg" radius="md">
        <Stack>
          <Group justify="space-between">
            <Text fw={600}>Sottoscrizione corrente</Text>
            <Group gap="xs">
              {!currentSub && (
                <Button leftSection={<IconPlus size={14} />} onClick={subscribeModal.open} loading={subscribe.isPending}>
                  Nuova sottoscrizione
                </Button>
              )}
              {currentSub && status === 'PAUSED' && (
                <Button variant="light" color="green" leftSection={<IconPlayerPlay size={14} />} loading={lifecycle.isPending} onClick={() => lifecycle.mutate({ action: 'resume' })}>
                  Riprendi
                </Button>
              )}
              {currentSub && status && !['PAUSED', 'CANCELLED'].includes(status) && (
                <Button variant="light" color="orange" leftSection={<IconPlayerPause size={14} />} loading={lifecycle.isPending} onClick={() => lifecycle.mutate({ action: 'pause' })}>
                  Pausa
                </Button>
              )}
              {currentSub && status && status !== 'CANCELLED' && (
                <Button variant="light" color="red" leftSection={<IconX size={14} />} loading={lifecycle.isPending}
                  onClick={() => {
                    const reason = window.prompt('Motivo annullamento (opzionale):') ?? undefined;
                    lifecycle.mutate({ action: 'cancel', reason });
                  }}
                >
                  Annulla
                </Button>
              )}
            </Group>
          </Group>

          {!currentSub ? (
            <Alert color="gray" variant="light" icon={<IconHourglass size={14} />}>
              Nessuna sottoscrizione attiva.
            </Alert>
          ) : (
            <Table>
              <Table.Tbody>
                <Table.Tr><Table.Td><Text size="sm" c="dimmed">Piano</Text></Table.Td><Table.Td>{currentSub.plan?.name ?? currentSub.plan?.code ?? '—'}</Table.Td></Table.Tr>
                <Table.Tr><Table.Td><Text size="sm" c="dimmed">Inizio</Text></Table.Td><Table.Td>{currentSub.startDate ? dayjs(currentSub.startDate).format('DD/MM/YYYY') : '—'}</Table.Td></Table.Tr>
                <Table.Tr><Table.Td><Text size="sm" c="dimmed">Fine</Text></Table.Td><Table.Td>{currentSub.endDate ? dayjs(currentSub.endDate).format('DD/MM/YYYY') : '—'}</Table.Td></Table.Tr>
                <Table.Tr><Table.Td><Text size="sm" c="dimmed">Trial fino al</Text></Table.Td><Table.Td>{currentSub.trialEndsAt ? dayjs(currentSub.trialEndsAt).format('DD/MM/YYYY') : '—'}</Table.Td></Table.Tr>
                <Table.Tr><Table.Td><Text size="sm" c="dimmed">Seats</Text></Table.Td><Table.Td>{currentSub.seats ?? '—'}</Table.Td></Table.Tr>
                <Table.Tr><Table.Td><Text size="sm" c="dimmed">Valuta · periodo</Text></Table.Td><Table.Td>{currentSub.currency ?? '—'} · {currentSub.billingPeriod ?? '—'}</Table.Td></Table.Tr>
                <Table.Tr><Table.Td><Text size="sm" c="dimmed">Auto-rinnovo</Text></Table.Td><Table.Td>{currentSub.autoRenew ? 'Sì' : 'No'}</Table.Td></Table.Tr>
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Card>

      {currentSub && (
        <Card withBorder padding="md" radius="md">
          <Group justify="space-between">
            <div>
              <Text fw={600}>Cambia piano</Text>
              <Text size="xs" c="dimmed">Upgrade o downgrade istantaneo. Il BE valida la transizione (potrebbe restituire <code>INVALID_TRANSITION</code>).</Text>
            </div>
            <Group>
              {plans.filter((p) => p.code && p.code !== currentSub.plan?.code).map((p) => (
                <Button
                  key={p.id}
                  size="xs"
                  variant="light"
                  leftSection={<IconArrowUp size={12} />}
                  loading={upgrade.isPending}
                  onClick={() => upgrade.mutate(p.code ?? '')}
                >
                  → {p.name ?? p.code}
                </Button>
              ))}
            </Group>
          </Group>
        </Card>
      )}

      <Card withBorder padding="md" radius="md">
        <Group justify="space-between">
          <div>
            <Text fw={600}>Promo / Sconti</Text>
            <Text size="xs" c="dimmed">Applica un codice promozionale alla sottoscrizione corrente o revoca quello attivo.</Text>
          </div>
          <Group>
            <Button size="xs" variant="light" leftSection={<IconTag size={12} />} onClick={promoModal.open}>Applica promo</Button>
            <Button size="xs" variant="subtle" color="red" loading={revokePromo.isPending} onClick={() => revokePromo.mutate(undefined)}>Revoca</Button>
          </Group>
        </Group>
      </Card>

      <Accordion variant="separated" defaultValue="licenses">
        <Accordion.Item value="licenses">
          <Accordion.Control>Licenze del tenant ({licenses.length})</Accordion.Control>
          <Accordion.Panel>
            {licensesQ.isLoading ? (
              <Center py="md"><Loader size="sm" /></Center>
            ) : licenses.length === 0 ? (
              <Text size="sm" c="dimmed">Nessuna licenza registrata. Il tenant userà solo le licenze derivanti dal piano corrente.</Text>
            ) : (
              <Table verticalSpacing="xs" horizontalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>License</Table.Th>
                    <Table.Th>Origine</Table.Th>
                    <Table.Th>Stato</Table.Th>
                    <Table.Th>Valida da</Table.Th>
                    <Table.Th>Valida fino a</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {licenses.map((l) => (
                    <Table.Tr key={l.id}>
                      <Table.Td>
                        <Text size="sm" fw={500}>{l.license?.name ?? l.license?.code ?? l.license?.id}</Text>
                        {l.license?.code && <Text size="xs" c="dimmed" ff="monospace">{l.license.code}</Text>}
                      </Table.Td>
                      <Table.Td><Badge size="xs" variant="light">{l.origin ?? '—'}</Badge></Table.Td>
                      <Table.Td><Badge size="xs" color={l.status === 'ACTIVE' ? 'green' : 'gray'}>{l.status ?? '—'}</Badge></Table.Td>
                      <Table.Td>{l.validFrom ? dayjs(l.validFrom).format('DD/MM/YYYY') : '—'}</Table.Td>
                      <Table.Td>{l.validUntil ? dayjs(l.validUntil).format('DD/MM/YYYY') : '∞'}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="history">
          <Accordion.Control>
            <Group gap={6}><IconHistory size={14} /> Storico eventi licensing</Group>
          </Accordion.Control>
          <Accordion.Panel>
            {historyQ.isLoading ? (
              <Center py="md"><Loader size="sm" /></Center>
            ) : (historyQ.data?.data ?? []).length === 0 ? (
              <Text size="sm" c="dimmed">Nessun evento registrato.</Text>
            ) : (
              <Table verticalSpacing="xs" horizontalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Data</Table.Th>
                    <Table.Th>Azione</Table.Th>
                    <Table.Th>Dettagli</Table.Th>
                    <Table.Th>Eseguita da</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(historyQ.data?.data ?? []).map((h) => (
                    <Table.Tr key={h.id}>
                      <Table.Td>{h.createdAt ? dayjs(h.createdAt).format('DD/MM/YYYY HH:mm') : '—'}</Table.Td>
                      <Table.Td><Badge size="xs" variant="light">{h.action ?? '—'}</Badge></Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">{h.details ?? '—'}</Text></Table.Td>
                      <Table.Td>{h.performedBy ?? '—'}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      <Modal opened={subscribeOpened} onClose={subscribeModal.close} title="Nuova sottoscrizione" centered>
        <form onSubmit={subscribeForm.onSubmit((v) => { subscribe.mutate(v); subscribeModal.close(); })}>
          <Stack>
            <Select label="Piano" required data={plans.map((p) => ({ value: p.code ?? '', label: p.name ?? p.code ?? '' }))} {...subscribeForm.getInputProps('planCode')} />
            <Group grow>
              <Select label="Valuta" data={['EUR', 'USD', 'GBP', 'CHF'].map((c) => ({ value: c, label: c }))} {...subscribeForm.getInputProps('currency')} />
              <Select label="Periodo" data={[{ value: 'monthly', label: 'Mensile' }, { value: 'yearly', label: 'Annuale' }]} {...subscribeForm.getInputProps('billingPeriod')} />
            </Group>
            <NumberInput label="Seats" min={1} {...subscribeForm.getInputProps('seats')} />
            <Switch label="Inizia in trial" {...subscribeForm.getInputProps('isTrial', { type: 'checkbox' })} />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={subscribeModal.close}>Annulla</Button>
              <Button type="submit" loading={subscribe.isPending}>Crea</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal opened={promoOpened} onClose={promoModal.close} title="Applica codice promozionale" centered>
        <form onSubmit={promoForm.onSubmit(({ promoCode }) => { applyPromo.mutate(promoCode); promoModal.close(); })}>
          <Stack>
            <TextInput label="Codice promo" required placeholder="WELCOME20" {...promoForm.getInputProps('promoCode')} />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={promoModal.close}>Annulla</Button>
              <Button type="submit" loading={applyPromo.isPending}>Applica</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
